"use client";

import React from "react";
import Link from "next/link";
import { LuBrain, LuMessageSquare, LuArrowRight, LuBookOpen } from "react-icons/lu";

export default function ResourcesPage() {
  const resources = [
    {
      title: "What is GEO?",
      subtitle: "Generative Engine Optimization",
      description:
        "Learn how to optimize your brand for AI-powered search engines like ChatGPT, Claude, Perplexity, and more. Understand the shift from SEO to GEO.",
      href: "/what-is-geo",
      icon: <LuBrain className="text-4xl text-accent" />,
      tags: ["AI Search", "Brand Visibility", "GEO Score"],
    },
    {
      title: "What is AEO?",
      subtitle: "Answer Engine Optimization",
      description:
        "Discover how to make your content the definitive answer that AI assistants cite and recommend. Stop competing for clicks - start owning the answer.",
      href: "/what-is-aeo",
      icon: <LuMessageSquare className="text-4xl text-accent" />,
      tags: ["Direct Answers", "Citations", "Content Strategy"],
    },
  ];

  return (
    <div className="min-h-screen bg-surface">
      

      {/* Hero Section */}
      <section className="w-full flex justify-center pt-12 md:pt-16 lg:pt-24 pb-12 md:pb-16 bg-surface">
        <div className="w-full max-w-[1400px] px-4 md:px-8 lg:px-14">
          <div className="flex flex-col items-center gap-6">
            <div>
              <span className="inline-block px-4 py-2 bg-surface-raised border border-hairline text-ink-muted rounded-full font-raleway font-semibold text-base">
                Learning Center
              </span>
            </div>

            <h1 className="font-raleway font-medium text-ink text-center text-3xl md:text-4xl lg:text-[56px] leading-tight lg:leading-[67.2px]">
              Resources & <span className="text-accent">Guides</span>
            </h1>

            <p className="font-raleway font-medium text-lg text-ink-muted text-center max-w-[700px]">
              Everything you need to understand and master AI search optimization. Learn about GEO, AEO, and how to make your brand visible in the age of AI.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="w-full flex justify-center py-16 md:py-20 lg:py-24">
        <div className="w-full max-w-[1400px] px-4 md:px-8 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {resources.map((resource, index) => (
              <Link
                key={index}
                href={resource.href}
                className="group bg-surface-raised rounded-3xl p-8 border border-hairline hover:border-accent hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col h-full">
                  <div className="mb-6">{resource.icon}</div>

                  <div className="mb-2">
                    <span className="text-sm font-raleway font-semibold text-accent uppercase tracking-wide">
                      {resource.subtitle}
                    </span>
                  </div>

                  <h2 className="font-raleway font-bold text-2xl md:text-3xl text-ink mb-4 group-hover:text-accent transition-colors">
                    {resource.title}
                  </h2>

                  <p className="font-raleway font-medium text-base text-ink-muted mb-6 flex-grow">
                    {resource.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {resource.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 bg-surface border border-hairline text-ink-muted font-raleway font-medium text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 text-accent font-raleway font-semibold">
                    <span>Read Guide</span>
                    <LuArrowRight className="text-xl group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources Section */}
      <section className="w-full flex justify-center py-16 md:py-20 lg:py-24 bg-surface">
        <div className="w-full max-w-[1400px] px-4 md:px-8 lg:px-14">
          <div className="text-center mb-12">
            <h2 className="font-raleway font-medium text-ink text-3xl md:text-4xl mb-4">
              More Resources
            </h2>
            <p className="font-raleway font-medium text-base text-ink-muted">
              Explore our blog and FAQ for more insights
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[800px] mx-auto">
            <Link
              href="/blogs"
              className="flex items-center gap-4 bg-surface-raised rounded-2xl p-6 border border-hairline hover:border-accent hover:shadow-lg transition-all group"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                <LuBookOpen className="text-2xl text-accent" />
              </div>
              <div className="flex-grow">
                <h3 className="font-raleway font-bold text-lg text-ink group-hover:text-accent transition-colors">
                  Blog
                </h3>
                <p className="font-raleway font-medium text-sm text-ink-muted">
                  Latest insights on AI search
                </p>
              </div>
              <LuArrowRight className="text-xl text-accent group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              href="/faq"
              className="flex items-center gap-4 bg-surface-raised rounded-2xl p-6 border border-hairline hover:border-accent hover:shadow-lg transition-all group"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                <LuMessageSquare className="text-2xl text-accent" />
              </div>
              <div className="flex-grow">
                <h3 className="font-raleway font-bold text-lg text-ink group-hover:text-accent transition-colors">
                  FAQs
                </h3>
                <p className="font-raleway font-medium text-sm text-ink-muted">
                  Common questions answered
                </p>
              </div>
              <LuArrowRight className="text-xl text-accent group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      
    </div>
  );
}
